/**
 * Report service — centralises all report-related API calls.
 *
 * Covers: report generation, report listing, data summaries,
 * label generation, completed reports, and admin reports.
 */
import api from './api'

// ---------------------------------------------------------------------------
// Report Retrieval
// ---------------------------------------------------------------------------

export function getReports(campaignId, batchId) {
  if (batchId !== undefined) {
    return api.get(`/reports/${campaignId}/${batchId}`)
  }
  return api.get(`/reports/${campaignId}`)
}

/**
 * Generate a report.
 *
 * `dateRange` is optional and narrows the report to orders CREATED within
 * it (`{ startDate, endDate }`, each `YYYY-MM-DD`). Either end may be
 * empty, which means unbounded on that side; omitting the argument
 * entirely leaves the report unfiltered.
 *
 * Empty strings are dropped rather than sent, so a cleared control
 * produces a bare URL and cannot be mistaken server-side for a real
 * bound.
 */
export function getReport(
  reportSlug,
  campaignId,
  reportFormat,
  reportName,
  batchId,
  dateRange = {}
) {
  return api.get(
    `/reports/${reportSlug}/${campaignId}/${reportFormat}/${reportName}/${batchId}`,
    { params: dateRangeParams(dateRange) }
  )
}

/**
 * Build the query params for a date range, omitting empty ends.
 *
 * Shared by `getReport` and `getReportList` so the two cannot disagree
 * about what an empty date means.
 */
export function dateRangeParams({ startDate, endDate } = {}) {
  const params = {}
  if (startDate) {
    params.start_date = startDate
  }
  if (endDate) {
    params.end_date = endDate
  }
  return params
}

export function getReportList(reportSlug, campaignId, batchId, dateRange = {}) {
  return api.get(`/reports/list/${reportSlug}/${campaignId}/${batchId}`, {
    params: dateRangeParams(dateRange)
  })
}

// ---------------------------------------------------------------------------
// Report Download
// ---------------------------------------------------------------------------

/**
 * MIME type + file extension per report format.
 *
 * xlsx is BINARY, so it must be requested as an arraybuffer -- axios
 * defaults to string parsing, which corrupts the bytes into a JS string
 * and yields a file Excel refuses to open.
 */
const FORMAT_META = {
  csv: { mime: 'text/csv', ext: 'csv', responseType: 'text' },
  pdf: { mime: 'application/pdf', ext: 'pdf', responseType: 'arraybuffer' },
  xlsx: {
    mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ext: 'xlsx',
    responseType: 'arraybuffer',
  },
  json: { mime: 'application/json', ext: 'json', responseType: 'text' },
}

/**
 * Hand a fetched report to the browser as a file.
 *
 * The anchor is APPENDED to the document before being clicked and removed
 * afterwards. A detached anchor is ignored outright by some browsers, and
 * appending costs nothing -- this is the difference between a click that
 * saves a file and one that silently does nothing.
 */
export function saveReportFile(content, filename, mime) {
  const blob = new Blob([content], { type: mime || 'application/octet-stream' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Revoking immediately can cancel an in-flight save in some browsers,
  // so this is deferred rather than synchronous.
  window.setTimeout(() => window.URL.revokeObjectURL(url), 30000)
}

/**
 * Turn an arbitrary string into something safe for a download filename.
 *
 * Store and report names are USER-AUTHORED and reach a filesystem here, so
 * path separators and control characters are replaced rather than trusted.
 */
export function reportFilename(parts, ext) {
  const raw = parts.filter(Boolean).join(' - ')
  const safe = raw
    .split('')
    .map((c) => (/[A-Za-z0-9 \-_.()]/.test(c) ? c : '-'))
    .join('')
    .trim()
  return `${safe || 'report'}.${ext}`
}

/**
 * Generate and DOWNLOAD every report file for a selection.
 *
 * TWO endpoints are involved and conflating them is GH #3692: this route's
 * `report_name` segment must carry a name from `/reports/list/...`, NOT the
 * report TYPE's display label from `/reports/<campaign>/<batch>`. Sending
 * the label makes the server match nothing and return **HTTP 200 with a
 * zero-byte body** -- a silent no-op that looks like a broken download.
 *
 * A report type can expand to SEVERAL named files (e.g. "Fulfillment PO",
 * "Supplier Summary", "Sales Breakdown (To Invoice)"), so this downloads
 * one file per name rather than assuming a single document.
 *
 * Returns the number of files saved. ZERO means the server produced no
 * content for any name -- the caller should surface that rather than
 * report success, because a silent zero is exactly how this bug hid.
 */
export async function downloadReport(
  reportSlug,
  campaignId,
  reportFormat,
  batchId,
  { storeName = '', reportTypeName = '', dateRange = {} } = {}
) {
  const meta = FORMAT_META[reportFormat] || FORMAT_META.csv
  const listed = await getReportList(reportSlug, campaignId, batchId, dateRange)
  const names = Array.isArray(listed && listed.data) ? listed.data : []
  // No names means this selection has nothing to render. Reported as 0 so
  // the caller can say so; emitting an empty file would be worse.
  if (!names.length) {
    return 0
  }

  // xlsx is a WHOLE-WORKBOOK format: the server puts every table on its own
  // sheet and deliberately ignores report_name (see campaign_report.to_xlsx).
  // So every name returns the SAME bytes -- verified: three requests, one
  // md5. Requesting once avoids saving three identical files.
  const perFileNames = reportFormat === 'xlsx' ? names.slice(0, 1) : names

  let saved = 0
  for (const name of perFileNames) {
    const res = await api.get(
      `/reports/${reportSlug}/${campaignId}/${reportFormat}/${encodeURIComponent(
        name
      )}/${batchId}`,
      { params: dateRangeParams(dateRange), responseType: meta.responseType }
    )
    const content = res && res.data
    const empty =
      content === undefined ||
      content === null ||
      content === '' ||
      (content.byteLength !== undefined && content.byteLength === 0)
    if (empty) {
      continue
    }
    saveReportFile(
      content,
      reportFilename(
        [storeName, reportFormat === 'xlsx' ? reportTypeName || name : name],
        meta.ext
      ),
      meta.mime
    )
    saved += 1
  }
  return saved
}

export function getCompletedReports() {
  return api.get('/reports/get_completed_reports')
}

export function getDataSummary(campaignId) {
  return api.get(`/reports/datasummary/${campaignId}`)
}

// ---------------------------------------------------------------------------
// Report Generation
// ---------------------------------------------------------------------------

export function generateLabels(data) {
  return api.post('/reports/generateLabels', data)
}
